import { PaymentTypeEnum } from '@msc-b2c/interfaces/enums';
import { OBSObjI, PaymentInfoSelected, PrepaidPaymentProps } from '@msc-b2c/interfaces/types';
import { PaymentSection, SkeletonLoader, Text } from '@msc-b2c/shared-components';
import {
  AppDispatch,
  closeLoader,
  getActiveSession,
  getCreditCards,
  getCurrencyCode,
  getOtherPaymentMethods,
  getPaymentButtons,
  mscGet,
  openLoader,
  PaymentContext,
  RootState,
  selectedPaymentInfo,
  useGetBookingQuery,
} from '@msc-b2c/shared-utils';
import { skipToken } from '@reduxjs/toolkit/query';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from '../../../payment/payment.module.scss';
import { triggerAddPaymentInfoEvent } from '../../../utils/myAreaAnalytics';
import clsx from 'clsx';

export function Payment({
  paymentMethodsTitle,
  paymentSectionTitle,
  paymentSectionDescription,
  paymentSectionFreeCartDescription,
  dateFormat,
  cardPaymentDetailsLabel,
  cardNumberPlaceholder,
  cardExpirationDatePlaceholder,
  cardCvvPlaceholder,
  cardCvvDisclaimer,
  mandatoryLabel,
  termsAndConditionSection,
  paymentButtonCTA,
  datatrans,
  enableAvs,
  avsFormInputField,
}: Readonly<PrepaidPaymentProps>) {
  const dispatch = useDispatch<AppDispatch>();
  const { onPriceToBook } = useContext(PaymentContext);

  const { is3dsFlow } = useSelector((state: RootState) => state.payment);
  const pricesResponse = useSelector((state: RootState) => state.myAreaCart?.prices);

  useEffect(() => {
    if (is3dsFlow) return;
    if (pricesResponse?.price?.total?.transactionId) return;
    onPriceToBook();
  }, []);
  const cart = useSelector((state: RootState) => state.myAreaCart.items || []);
  const onboardServices = useSelector(
    (state: RootState) => state.myCruise.obs?.onboardServices || []
  );
  const excursions = useSelector((state: RootState) => state.myCruise.excursions?.excursions || []);
  const { cabinDetails, membershipData } = useSelector((state: RootState) => state.myCruise);
  const appliedECoupon = useSelector((state: RootState) => state.myAreaCart.appliedECoupon);
  const { alreadyPurchasedSectionRouteMapping } = useSelector(
    (state: RootState) => state.navigationMenuData
  );
  const { passengerId = '', bookingNumber = '' } = getActiveSession();
  const { data: booking } = useGetBookingQuery(bookingNumber || skipToken);
  const defaultPaymentMethod = getCreditCards() ? 'card' : getPaymentButtons() ? 'button' : 'other';

  useEffect(() => {
    if (!mscGet<string>('session', 'paymentAmount')) {
      const firstLoadPaymentInfo: PaymentInfoSelected = {
        type: PaymentTypeEnum.FULL,
        price: pricesResponse?.price?.total?.afterDiscount?.toString() ?? '',
        paymentMethod: defaultPaymentMethod,
      };
      dispatch(selectedPaymentInfo(firstLoadPaymentInfo));
      closeLoader('.loader');
    } else if (
      mscGet<string>('session', 'paymentAmount') &&
      mscGet<string>('session', 'paymentType')
    ) {
      if (!!pricesResponse) openLoader('.loader');
      const paymentInfoInSession: PaymentInfoSelected = {
        type: mscGet<string>('session', 'paymentType') ?? '',
        price: mscGet<string>('session', 'paymentAmount') ?? '',
        paymentMethod: defaultPaymentMethod,
      };
      dispatch(selectedPaymentInfo(paymentInfoInSession));
    }
  }, [pricesResponse]);

  const mopAvailable = useMemo(() => {
    const creditCardCodes = getCreditCards().map((paymentInfo) => paymentInfo.type);
    const paymentButtonCodes = getPaymentButtons().map((paymentInfo) => paymentInfo.type);
    const otherPaymentCodes = getOtherPaymentMethods().map((paymentInfo) => paymentInfo.type);
    return [...creditCardCodes, ...paymentButtonCodes, ...otherPaymentCodes]
      .filter((code) => !!code)
      .map((code) => code.toLowerCase())
      .join('|');
  }, []);

  const enrichedItems = useMemo(() => {
    const excursionsByCode = new Map(excursions.map((excursion) => [excursion.code, excursion]));
    const onboardServicesByCode = new Map(
      onboardServices.map((onboardService) => [onboardService.code, onboardService])
    );
    return cart
      .map((cartItem) => {
        const card =
          cartItem.itemType === 'EXCURSION'
            ? excursionsByCode.get(cartItem.code)
            : onboardServicesByCode.get(cartItem.code);
        if (!card) return undefined;
        const itemCategory2 =
          cartItem.itemType === 'EXCURSION'
            ? 'excursions'
            : alreadyPurchasedSectionRouteMapping
                ?.find((routeItem) =>
                  Object.keys(routeItem.routeTypes).includes((card as OBSObjI).type?.code ?? '')
                )
                ?.routeLabel.toLowerCase() ?? '';
        return {
          ...card,
          itemCategory2,
          itemListName: cartItem.itemListName,
          selectedPassengerQuantity: cartItem.passengers?.length,
        };
      })
      .filter((card) => !!card);
  }, [cart, excursions, onboardServices, alreadyPurchasedSectionRouteMapping]);

  const hasTriggeredAddPaymentInfoRef = useRef(false);

  useEffect(() => {
    if (hasTriggeredAddPaymentInfoRef.current) return;
    if (!booking || !pricesResponse) return;

    triggerAddPaymentInfoEvent({
      booking,
      loggedUserId: passengerId,
      items: enrichedItems,
      cabinDetails,
      currency: getCurrencyCode(),
      value: pricesResponse?.price?.total?.afterDiscount || '0',
      mopAvailable,
      appliedECoupon,
      membershipData,
    });
    hasTriggeredAddPaymentInfoRef.current = true;
  }, [
    booking,
    pricesResponse,
    enrichedItems,
    cabinDetails,
    mopAvailable,
    appliedECoupon,
    passengerId,
  ]);

  return (
    <div className={clsx(styles['payment_component'], styles['-my-area'])}>
      <div className={styles['payment__container']}>
        <div className={styles['payment__options']}>
          <div className={styles['payment__options-title']}>
            <Text
              text={paymentMethodsTitle ?? ''}
              styleOption={'header-xs'}
            />
          </div>

          {(!!pricesResponse && datatrans) || is3dsFlow || (cart.length > 0 && datatrans) ? (
            <PaymentSection
              sectionTitle={paymentSectionTitle ?? ''}
              sectionDescription={paymentSectionDescription ?? ''}
              sectionFreeCartDescription={paymentSectionFreeCartDescription ?? ''}
              dateFormat={dateFormat ?? ''}
              cardPaymentDetailsLabel={cardPaymentDetailsLabel ?? ''}
              cardNumberPlaceholder={cardNumberPlaceholder ?? ''}
              cardExpirationDatePlaceholder={cardExpirationDatePlaceholder ?? ''}
              cardCvvPlaceholder={cardCvvPlaceholder ?? ''}
              cardCvvDisclaimer={cardCvvDisclaimer ?? ''}
              mandatoryLabel={mandatoryLabel ?? ''}
              termsAndConditionSection={termsAndConditionSection ?? ''}
              paymentButtonCTA={paymentButtonCTA ?? ''}
              datatrans={datatrans}
              priceCurrencyOrder={''}
              type={PaymentTypeEnum.FULL}
              creditCards={[]}
              paymentButtons={[]}
              otherPaymentMethods={[]}
              transactionId={pricesResponse?.price?.total?.transactionId || ''}
              amount={pricesResponse?.price?.total?.afterDiscount?.toString() || ''}
              totalAmount={pricesResponse?.price?.total?.afterDiscount?.toString() || ''}
              isFreeCartEnabled={true}
              enableAvs={enableAvs}
              avsFormInputField={avsFormInputField}
            />
          ) : (
            <SkeletonLoader
              height={'300px'}
              styleOption="cmp-skeleton__loader"
            />
          )}
        </div>
      </div>
    </div>
  );
}
